<script>
import { mapGetters } from 'vuex';
import InboxesAPI from 'dashboard/api/inboxes';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { REDIS_FEATURE_FLAGS } from 'dashboard/featureFlags';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';

export const SOFTPHONE_URL = 'https://voip.atendesimples.com/';
export const SOFTPHONE_WINDOW = {
  NAME: 'atendesimples-softphone',
  FEATURES: 'popup,width=420,height=720',
};
export const DOCK_LAYOUT = {
  DEFAULT_WIDTH: 384,
  DEFAULT_HEIGHT: 640,
  MIN_WIDTH: 320,
  MAX_WIDTH: 720,
  MIN_HEIGHT: 480,
  EDGE_PADDING: 8,
  LAUNCHER_CLEARANCE: 72,
  STORAGE_KEY: 'voiceSoftphoneDock.layout',
  PERSIST_DEBOUNCE_MS: 250,
  RINGING_TIMEOUT_MS: 45000,
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const viewport = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

export const clampLayout = layout => {
  const { width: vw, height: vh } = viewport();
  const { EDGE_PADDING, MIN_WIDTH, MAX_WIDTH, MIN_HEIGHT } = DOCK_LAYOUT;
  const width = clamp(
    layout.width,
    MIN_WIDTH,
    Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, vw - EDGE_PADDING * 2))
  );
  const height = clamp(
    layout.height,
    MIN_HEIGHT,
    Math.max(MIN_HEIGHT, vh - EDGE_PADDING * 2)
  );
  return {
    width,
    height,
    left: clamp(
      layout.left,
      EDGE_PADDING,
      Math.max(EDGE_PADDING, vw - width - EDGE_PADDING)
    ),
    bottom: clamp(
      layout.bottom,
      EDGE_PADDING,
      Math.max(EDGE_PADDING, vh - height - EDGE_PADDING)
    ),
  };
};

export const defaultLayout = () => {
  const { DEFAULT_WIDTH, DEFAULT_HEIGHT, EDGE_PADDING, LAUNCHER_CLEARANCE } =
    DOCK_LAYOUT;
  return clampLayout({
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    left: viewport().width - DEFAULT_WIDTH - EDGE_PADDING * 3,
    bottom: LAUNCHER_CLEARANCE,
  });
};

const readStoredLayout = () => {
  try {
    const raw = localStorage.getItem(DOCK_LAYOUT.STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const isNumber = value => typeof value === 'number' && !Number.isNaN(value);
    if (
      parsed &&
      ['width', 'height', 'left', 'bottom'].every(key => isNumber(parsed[key]))
    ) {
      return clampLayout(parsed);
    }
  } catch (error) {
    return null;
  }
  return null;
};

export const SOFTPHONE_LOAD_TIMEOUT_MS = 10000;

const MANUAL_FALLBACK = { url: SOFTPHONE_URL, mode: 'manual', reason: 'error' };

export const isSoftphoneFallback = softphone => softphone?.reason === 'error';

export const fetchSoftphone = async inboxId => {
  let timer;
  const expired = new Promise((resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error('softphone_timeout')),
      SOFTPHONE_LOAD_TIMEOUT_MS
    );
  });
  const request = InboxesAPI.getVoiceSoftphone(inboxId);
  request.catch(() => {});
  try {
    const { data } = await Promise.race([request, expired]);
    return data;
  } catch (error) {
    return { ...MANUAL_FALLBACK };
  } finally {
    clearTimeout(timer);
  }
};

const detachedWindow = { current: null, inboxId: null };

export const hasDetachedSoftphoneWindow = () =>
  Boolean(detachedWindow.current && !detachedWindow.current.closed);

export const focusDetachedSoftphoneWindow = () => {
  if (hasDetachedSoftphoneWindow()) detachedWindow.current.focus();
};

export const forgetDetachedSoftphoneWindow = () => {
  detachedWindow.current = null;
  detachedWindow.inboxId = null;
};

export const closeDetachedSoftphoneWindow = () => {
  if (hasDetachedSoftphoneWindow()) detachedWindow.current.close();
  forgetDetachedSoftphoneWindow();
};

export const pruneDetachedSoftphoneWindow = inboxIds => {
  if (!hasDetachedSoftphoneWindow()) return;
  if (!inboxIds.includes(detachedWindow.inboxId)) {
    closeDetachedSoftphoneWindow();
  }
};

export const openSoftphoneWindow = (url, inboxId) => {
  const popup = window.open(
    url,
    SOFTPHONE_WINDOW.NAME,
    SOFTPHONE_WINDOW.FEATURES
  );
  if (!popup) return null;
  popup.opener = null;
  detachedWindow.current = popup;
  detachedWindow.inboxId = inboxId;
  return popup;
};

const ringingKey = ({ inboxId, callId }) => `${inboxId}:${callId}`;

export default {
  name: 'VoiceSoftphoneDock',
  data() {
    return {
      isOpen: false,
      isFrameMounted: false,
      isLoading: false,
      softphone: null,
      softphoneInboxId: null,
      layout: readStoredLayout() || defaultLayout(),
      isMoving: false,
      isResizing: false,
      ringingKeys: [],
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      inboxes: 'inboxes/getInboxes',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    voiceInbox() {
      return this.inboxes.find(
        inbox => inbox.channel_type === INBOX_TYPES.VOICE
      );
    },
    voiceInboxIds() {
      return this.inboxes
        .filter(inbox => inbox.channel_type === INBOX_TYPES.VOICE)
        .map(inbox => inbox.id);
    },
    isEligible() {
      return (
        !!this.accountId &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          REDIS_FEATURE_FLAGS.CHANNEL_VOICE
        ) &&
        Boolean(this.voiceInbox)
      );
    },
    isOnSoftphonePage() {
      return Boolean(this.$route?.meta?.voiceSoftphonePage);
    },
    canShowDock() {
      return (
        this.isEligible &&
        !isOnOnboardingView(this.$route) &&
        !this.isOnSoftphonePage
      );
    },
    softphoneUrl() {
      return this.softphone?.url || SOFTPHONE_URL;
    },
    hintKey() {
      if (this.softphone?.mode === 'token') return 'VOICE_SOFTPHONE.HINT_TOKEN';
      if (this.softphone?.reason === 'no_extension')
        return 'VOICE_SOFTPHONE.HINT_NO_EXTENSION';
      return 'VOICE_SOFTPHONE.HINT';
    },
    hintParams() {
      return { extension: this.softphone?.extension || '' };
    },
    isFallback() {
      return isSoftphoneFallback(this.softphone);
    },
    panelStyle() {
      const { width, height, left, bottom } = this.layout;
      return {
        width: `${width}px`,
        height: `${height}px`,
        left: `${left}px`,
        bottom: `${bottom}px`,
      };
    },
    isInteracting() {
      return this.isMoving || this.isResizing;
    },
    isRinging() {
      return this.ringingKeys.length > 0;
    },
  },
  watch: {
    isOnSoftphonePage(onPage) {
      if (onPage) this.closeSession();
    },
    accountId() {
      this.resetSession();
    },
    voiceInboxIds(inboxIds) {
      if (this.softphoneInboxId && !inboxIds.includes(this.softphoneInboxId)) {
        this.resetSession({ closeWindow: false });
      }
      this.pruneRinging(inboxIds);
      pruneDetachedSoftphoneWindow(inboxIds);
    },
  },
  mounted() {
    window.addEventListener('resize', this.reclampLayout);
    this.$_ringingTimers = {};
    this.$_lastSeen = {};
    this.$emitter.on(BUS_EVENTS.VOICE_CALL_RINGING, this.onCallRinging);
    this.$emitter.on(BUS_EVENTS.VOICE_CALL_SETTLED, this.onCallSettled);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.reclampLayout);
    this.$emitter.off(BUS_EVENTS.VOICE_CALL_RINGING, this.onCallRinging);
    this.$emitter.off(BUS_EVENTS.VOICE_CALL_SETTLED, this.onCallSettled);
    this.clearRinging();
    this.stopPointerTracking();
    clearTimeout(this.$_persistTimer);
  },
  methods: {
    isStale(key, updatedAt) {
      const last = this.$_lastSeen[key];
      if (updatedAt && last && updatedAt < last) return true;
      if (updatedAt) this.$_lastSeen[key] = updatedAt;
      return false;
    },
    onCallRinging(event = {}) {
      const { callId, inboxId, updatedAt } = event;
      if (!callId || !this.voiceInboxIds.includes(inboxId)) return;
      const key = ringingKey(event);
      if (this.isStale(key, updatedAt)) return;
      if (!this.ringingKeys.includes(key)) {
        this.ringingKeys = [...this.ringingKeys, key];
      }
      clearTimeout(this.$_ringingTimers[key]);
      this.$_ringingTimers[key] = setTimeout(
        () => this.forgetRinging(key),
        DOCK_LAYOUT.RINGING_TIMEOUT_MS
      );
    },
    onCallSettled(event = {}) {
      if (!event.callId) return;
      const key = ringingKey(event);
      if (this.isStale(key, event.updatedAt)) return;
      this.forgetRinging(key);
    },
    forgetRinging(key) {
      clearTimeout(this.$_ringingTimers[key]);
      delete this.$_ringingTimers[key];
      this.ringingKeys = this.ringingKeys.filter(item => item !== key);
    },
    pruneRinging(inboxIds) {
      this.ringingKeys
        .filter(key => !inboxIds.includes(Number(key.split(':')[0])))
        .forEach(key => this.forgetRinging(key));
    },
    clearRinging() {
      Object.values(this.$_ringingTimers || {}).forEach(clearTimeout);
      this.$_ringingTimers = {};
      this.$_lastSeen = {};
      this.ringingKeys = [];
    },
    closeSession() {
      this.isOpen = false;
      this.isFrameMounted = false;
    },
    resetSession({ closeWindow = true } = {}) {
      this.closeSession();
      this.$_loadSequence = (this.$_loadSequence || 0) + 1;
      this.isLoading = false;
      this.softphone = null;
      this.softphoneInboxId = null;
      this.clearRinging();
      if (closeWindow) closeDetachedSoftphoneWindow();
    },
    async toggle() {
      if (hasDetachedSoftphoneWindow()) {
        focusDetachedSoftphoneWindow();
        useAlert(this.$t('VOICE_SOFTPHONE.WINDOW_ALREADY_OPEN'));
        return;
      }
      this.isOpen = !this.isOpen;
      if (!this.isOpen || this.isFrameMounted) return;
      const ready = await this.loadSoftphone();
      if (ready && this.isOpen && !hasDetachedSoftphoneWindow()) {
        this.isFrameMounted = true;
      }
    },
    async loadSoftphone() {
      const inbox = this.voiceInbox;
      if (!inbox) return false;
      if (
        this.softphone &&
        !isSoftphoneFallback(this.softphone) &&
        this.softphoneInboxId === inbox.id
      ) {
        return true;
      }
      this.isLoading = true;
      if (this.softphoneInboxId !== inbox.id) this.softphone = null;
      this.softphoneInboxId = inbox.id;
      this.$_loadSequence = (this.$_loadSequence || 0) + 1;
      const sequence = this.$_loadSequence;
      const result = await fetchSoftphone(inbox.id);
      if (sequence !== this.$_loadSequence) return false;
      this.softphone = result;
      this.isLoading = false;
      return true;
    },
    async retryLogin() {
      this.isFrameMounted = false;
      this.softphone = null;
      const ready = await this.loadSoftphone();
      if (ready && this.isOpen && !hasDetachedSoftphoneWindow()) {
        this.isFrameMounted = true;
      }
    },
    openInWindow() {
      if (!this.softphone) return;
      if (!openSoftphoneWindow(this.softphoneUrl, this.softphoneInboxId)) {
        useAlert(this.$t('VOICE_SOFTPHONE.POPUP_BLOCKED'));
        return;
      }
      this.closeSession();
    },
    reclampLayout() {
      this.applyLayout(this.layout);
    },
    resetLayout() {
      this.applyLayout(defaultLayout());
    },
    applyLayout(next) {
      this.layout = clampLayout(next);
      this.schedulePersist();
    },
    schedulePersist() {
      clearTimeout(this.$_persistTimer);
      this.$_persistTimer = setTimeout(
        this.persistLayout,
        DOCK_LAYOUT.PERSIST_DEBOUNCE_MS
      );
    },
    persistLayout() {
      try {
        localStorage.setItem(
          DOCK_LAYOUT.STORAGE_KEY,
          JSON.stringify(this.layout)
        );
      } catch (error) {
        this.$_persistError = error;
      }
    },
    startMove(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      const origin = { x: event.clientX, y: event.clientY, ...this.layout };
      this.trackPointer(move => {
        this.applyLayout({
          ...this.layout,
          left: origin.left + (move.clientX - origin.x),
          bottom: origin.bottom - (move.clientY - origin.y),
        });
      });
      this.isMoving = true;
    },
    startResize(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      const origin = { x: event.clientX, y: event.clientY, ...this.layout };
      this.trackPointer(move => {
        const deltaX = move.clientX - origin.x;
        const width = clamp(
          origin.width - deltaX,
          DOCK_LAYOUT.MIN_WIDTH,
          DOCK_LAYOUT.MAX_WIDTH
        );
        this.applyLayout({
          ...this.layout,
          width,
          height: origin.height - (move.clientY - origin.y),
          left: origin.left + (origin.width - width),
        });
      });
      this.isResizing = true;
    },
    trackPointer(onMove) {
      this.detachPointerHandlers();
      const onUp = () => this.stopPointerTracking();
      this.$_pointerHandlers = { onMove, onUp };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    detachPointerHandlers() {
      const handlers = this.$_pointerHandlers;
      if (!handlers) return;
      window.removeEventListener('pointermove', handlers.onMove);
      window.removeEventListener('pointerup', handlers.onUp);
      window.removeEventListener('pointercancel', handlers.onUp);
      this.$_pointerHandlers = null;
    },
    stopPointerTracking() {
      this.detachPointerHandlers();
      this.isMoving = false;
      this.isResizing = false;
    },
  },
};
</script>

<template>
  <div v-show="canShowDock" class="voice-softphone-dock">
    <template v-if="isEligible">
      <div
        v-show="isOpen"
        class="voice-softphone-dock__panel relative flex flex-col overflow-hidden rounded-3xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-md"
        :class="{ 'select-none': isInteracting }"
        :style="panelStyle"
        data-testid="softphone-panel"
      >
        <div
          v-if="isInteracting"
          class="voice-softphone-dock__shield"
          :class="isMoving ? 'cursor-grabbing' : 'cursor-nwse-resize'"
          data-testid="softphone-shield"
          aria-hidden="true"
        />
        <button
          type="button"
          class="voice-softphone-dock__resize group"
          data-testid="softphone-resize"
          :aria-label="$t('VOICE_SOFTPHONE.RESIZE')"
          :title="$t('VOICE_SOFTPHONE.RESIZE')"
          @pointerdown="startResize"
          @dblclick="resetLayout"
        >
          <span
            class="voice-softphone-dock__resize-grip"
            :class="{ 'opacity-100': isResizing }"
            aria-hidden="true"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <line
                x1="8"
                y1="1"
                x2="1"
                y2="8"
                stroke="currentColor"
                stroke-width="1.25"
                stroke-linecap="round"
              />
              <line
                x1="4"
                y1="1"
                x2="1"
                y2="4"
                stroke="currentColor"
                stroke-width="1.25"
                stroke-linecap="round"
              />
            </svg>
          </span>
        </button>
        <header
          class="flex items-center justify-between gap-3 px-6 py-[15px] border-b border-slate-100 dark:border-slate-700"
        >
          <h2
            class="m-0 text-base font-medium text-slate-800 dark:text-slate-50 truncate"
          >
            {{ $t('VOICE_SOFTPHONE.TITLE') }}
          </h2>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="voice-softphone-dock__action cursor-grab active:cursor-grabbing"
              :class="{ 'cursor-grabbing': isMoving }"
              data-testid="softphone-move"
              :aria-label="$t('VOICE_SOFTPHONE.MOVE')"
              :title="$t('VOICE_SOFTPHONE.MOVE')"
              @pointerdown="startMove"
              @dblclick="resetLayout"
            >
              <svg
                width="14"
                height="18"
                viewBox="0 0 12 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="3" cy="3" r="1.25" />
                <circle cx="9" cy="3" r="1.25" />
                <circle cx="3" cy="8" r="1.25" />
                <circle cx="9" cy="8" r="1.25" />
                <circle cx="3" cy="13" r="1.25" />
                <circle cx="9" cy="13" r="1.25" />
              </svg>
            </button>
            <button
              type="button"
              class="voice-softphone-dock__action"
              data-testid="softphone-open-window"
              :disabled="!softphone"
              :aria-label="$t('VOICE_SOFTPHONE.OPEN_WINDOW')"
              :title="$t('VOICE_SOFTPHONE.OPEN_WINDOW')"
              @click="openInWindow"
            >
              <fluent-icon icon="open" size="18" />
            </button>
            <button
              type="button"
              class="voice-softphone-dock__action"
              data-testid="softphone-minimize"
              :aria-label="$t('VOICE_SOFTPHONE.MINIMIZE')"
              :title="$t('VOICE_SOFTPHONE.MINIMIZE')"
              @click="toggle"
            >
              <fluent-icon icon="dismiss" size="18" />
            </button>
          </div>
        </header>
        <div
          v-if="isLoading"
          class="flex-1 flex items-center justify-center text-sm text-slate-600 dark:text-slate-300"
          data-testid="softphone-loading"
        >
          {{ $t('VOICE_SOFTPHONE.LOADING') }}
        </div>
        <iframe
          v-if="isFrameMounted"
          :src="softphoneUrl"
          :title="$t('VOICE_SOFTPHONE.TITLE')"
          class="voice-softphone-dock__frame flex-1 w-full border-0"
          :class="{ 'pointer-events-none': isInteracting }"
          allow="microphone; autoplay"
          data-testid="softphone-frame"
        />
        <p
          class="m-0 px-6 py-3 text-xs text-slate-600 dark:text-slate-300 border-t border-slate-100 dark:border-slate-700"
        >
          {{ $t(hintKey, hintParams) }}
          <button
            v-if="isFallback"
            type="button"
            class="ml-1 underline text-woot-500 hover:text-woot-600"
            data-testid="softphone-retry"
            @click="retryLogin"
          >
            {{ $t('VOICE_SOFTPHONE.RETRY') }}
          </button>
        </p>
      </div>
      <button
        type="button"
        class="voice-softphone-dock__launcher flex items-center gap-2 px-4 py-2 rounded-full bg-woot-500 text-white text-sm font-medium shadow-lg hover:bg-woot-600"
        :class="{ 'voice-softphone-dock__launcher--ringing': isRinging }"
        data-testid="softphone-toggle"
        :aria-expanded="isOpen ? 'true' : 'false'"
        @click="toggle"
      >
        <fluent-icon :icon="isOpen ? 'chevron-down' : 'call'" size="16" />
        {{
          isRinging
            ? $t('VOICE_SOFTPHONE.RINGING')
            : $t('VOICE_SOFTPHONE.TOGGLE')
        }}
      </button>
    </template>
  </div>
</template>

<style scoped lang="scss">
.voice-softphone-dock__launcher {
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 40;
}

.voice-softphone-dock__launcher--ringing {
  animation: voice-softphone-ring 1.2s ease-in-out infinite;
}

@keyframes voice-softphone-ring {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(255, 90, 31, 0.55);
    transform: scale(1);
  }

  50% {
    box-shadow: 0 0 0 12px rgba(255, 90, 31, 0);
    transform: scale(1.05);
  }
}

@media (prefers-reduced-motion: reduce) {
  .voice-softphone-dock__launcher--ringing {
    animation: none;
    box-shadow: 0 0 0 4px rgba(255, 90, 31, 0.45);
  }
}

.voice-softphone-dock__panel {
  position: fixed;
  z-index: 40;
  max-width: calc(100vw - 1rem);
  max-height: calc(100vh - 1rem);
}

.voice-softphone-dock__shield {
  position: fixed;
  inset: 0;
  z-index: 50;
}

.voice-softphone-dock__action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  border: 0;
  background: transparent;
  color: var(--s-600);
  transition:
    background-color 0.15s,
    color 0.15s;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &:hover,
  &:focus-visible {
    background: var(--s-75);
    color: var(--s-900);
  }

  .dark & {
    color: var(--s-300);
  }

  .dark &:hover,
  .dark &:focus-visible {
    background: var(--s-700);
    color: var(--s-25);
  }
}

.voice-softphone-dock__resize {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  cursor: nwse-resize;
  background: transparent;
  border: 0;

  &:focus {
    outline: none;
  }
}

.voice-softphone-dock__resize-grip {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-bottom-right-radius: 0.375rem;
  color: var(--s-500);
  opacity: 0.3;
  transition: opacity 0.15s;
  pointer-events: none;

  .group:hover &,
  .group:focus-visible & {
    opacity: 1;
    background: var(--w-75);
  }
}

.voice-softphone-dock__frame {
  min-height: 0;
}
</style>
